import { t } from '../i18n';

export type Environment = 'prod' | 'staging' | 'dev' | 'local';

export interface BuildInfo {
  /** Short commit the server was built from; "dev" when no server stamped it. */
  commit: string;
  /** Build time as stamped; empty when no server stamped it. */
  time: string;
}

function readMeta(name: string): string {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')?.trim() ?? '';
}

// The build identity reaches the page as boot-time metas stamped by
// cmd/server/spa.go from internal/version — never as a Vite define. A define
// bakes the value into main-<hash>.js, so a per-build value (a timestamp, the
// HEAD sha) renames the entry chunk on every build and the committed dist
// shell can never stay in sync (issue 518). Absent metas (the Vite dev
// server, the Capacitor shell) fall back to the same placeholders the
// server's version package defaults to.
export function buildInfo(): BuildInfo {
  return { commit: shortCommit(readMeta('fexo-build-commit')) || 'dev', time: readMeta('fexo-build-time') };
}

// Cloud Build stamps internal/version.Commit with the full 40-hex COMMIT_SHA
// (the admin Status lens shows it whole); the stamp is a glanceable id, so it
// shows the conventional 7-char prefix. Anything that is not a full hex sha
// ("dev", a tag, an already-short sha) is shown as-is.
function shortCommit(raw: string): string {
  return /^[0-9a-f]{40}$/i.test(raw) ? raw.slice(0, 7) : raw;
}

export function detectEnvironment(host: string = location.hostname): Environment {
  if (host === 'fexo-enterprise.com' || host === 'www.fexo-enterprise.com') return 'prod';
  if (host.startsWith('staging.')) return 'staging';
  if (host.startsWith('dev.')) return 'dev';
  return 'local';
}

export function buildStampText(env: Environment = detectEnvironment(), sha: string = buildInfo().commit): string {
  return t('build.stamp', { env, sha });
}

export function renderBuildStamp(parent: HTMLElement, className = 'login__build-stamp'): HTMLElement {
  const stamp = document.createElement('p');
  stamp.className = className;
  stamp.textContent = buildStampText();
  const { time } = buildInfo();
  if (time) stamp.title = t('build.builtAt', { time });
  parent.appendChild(stamp);
  return stamp;
}
